import { AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';
import React, { useEffect } from 'react';
import { Button, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { TriangleSlider } from '../../../slider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

const AppCriteriaTypeText = ({
    importance,
    value,
    hideValues,
    hideImportance,
    onSliderChange,
    onTradeoffValueChange,
    showAddConstraint,
    setCriteriaConstraints,
    criteriaConstraints,
    id,
    disabled = false,
}: {
    disabled?: boolean;
    importance: number;
    value: string;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    hideField?: boolean;
    hideValues?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: (value: any, id: string) => void;
    id: string;
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[]
    };
    setCriteriaConstraints: (value: (((prevState: {
        [p: string]: CriteriaConstraint[]
    }) => {
        [p: string]: CriteriaConstraint[]
    }) | {
                                         [p: string]: CriteriaConstraint[]
                                     })) => void;
}) => {
    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                { ...criteriaConstraints, [id]: [] }
            ));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConstraintChange = (value: string, index: number, key: 'value' | 'rule') => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    [key]: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onConstraintOperatorChange = (value: 'and' | 'or', index: number) => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    operator: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onAddConstraint = () => {
        setCriteriaConstraints(constraints => (
            {
                ...constraints,
                [id]: [ ...constraints[id], {
                    value: '',
                    rule: 'em',
                    operator: constraints[id].length > 0 ? 'and' : null,
                } ],
            }
        ));
    };

    const onDeleteConstraint = (index: number) => {
        setCriteriaConstraints(constraints => {
            const filtered = constraints[id].filter((_, idx) => idx !== index);
            if (!filtered.length) {
                return {
                    ...constraints,
                    [id]: [],
                };
            }

            filtered[0] = {
                ...filtered[0],
                operator: null,
            };

            return {
                ...constraints,
                [id]: filtered,
            };
        });
    };

    return (
        <div>
            {hideValues ? null : (
                <div style={{ marginBottom: 10 }}>
                    <TextField
                        defaultValue={value}
                        fullWidth
                        size="small"
                        onBlur={(e) => onTradeoffValueChange(e.target.value, id)}
                        disabled={disabled}
                    />
                </div>
            )}
            {hideImportance ? null : (
                <div>
                    <TriangleSlider
                        min={0}
                        max={100}
                        step={1}
                        value={importance}
                        leftText="Not Important"
                        rightText="Really Important"
                        onChange={(_, value) => onSliderChange(value as number, id)}
                        disabled={disabled}
                    />
                </div>
            )}
            {!showAddConstraint ? null : (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: criteriaConstraints[id].length > 0 ? 'block' : 'none' }}>
                        <FormControl sx={{ my: 2, minWidth: 120 }} size="small" disabled={disabled}>
                            {criteriaConstraints[id].map((constraint, index) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}
                                    key={id + '_' + index}
                                >
                                    {constraint.operator && (
                                        <Select
                                            style={{ marginRight: 16 }}
                                            variant="outlined"
                                            value={constraint.operator}
                                            onChange={(e) => onConstraintOperatorChange(e.target.value as ('and' | 'or'), index)}
                                        >
                                            <MenuItem value="and">And</MenuItem>
                                            <MenuItem value="or">Or</MenuItem>
                                        </Select>
                                    )}
                                    <Select
                                        variant="outlined"
                                        value={constraint.rule}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'rule')}
                                    >
                                        <MenuItem value="em">Include if exact match</MenuItem>
                                        <MenuItem value="!em">Exclude if exact match</MenuItem>
                                        <MenuItem value="instr">Include if contains</MenuItem>
                                        <MenuItem value="!instr">exclude if contains</MenuItem>
                                    </Select>
                                    <TextField
                                        style={{ marginLeft: 16 }}
                                        value={constraint.value}
                                        onBlur={(e) => onConstraintChange(e.target.value, index, 'value')}
                                        size="small"
                                    />
                                    <IconButton style={{ marginLeft: '.5rem' }}
                                                onClick={() => onDeleteConstraint(index)}>
                                        <CloseIcon/>
                                    </IconButton>
                                </div>
                            ))}
                        </FormControl>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        size="small"
                        onClick={onAddConstraint}
                        disabled={disabled}
                    >
                        Add Constraint
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AppCriteriaTypeText;